import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './Proposals.css';

function Proposals() {
    const { hasPermission } = useAuth();
    const [proposals, setProposals] = useState([]);
    const [deals, setDeals] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'create' or 'templates' or 'create-template'
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        validTill: '',
        clientName: '',
        contactName: '',
        deal: '',
        title: '',
        total: '',
        details: ''
    });

    const [templateFormData, setTemplateFormData] = useState({
        name: '',
        description: '',
        content: '',
        type: 'custom'
    });

    const fetchProposals = useCallback(async () => {
        try {
            const response = await api.get('/proposals');
            setProposals(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching proposals:', error);
            setLoading(false);
        }
    }, []);

    const fetchDeals = async () => {
        try {
            const response = await api.get('/deals');
            setDeals(response.data);
        } catch (error) {
            console.error('Error fetching deals:', error);
        }
    };

    const fetchTemplates = useCallback(async () => {
        try {
            const response = await api.get('/proposal-templates');
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    }, []);

    useEffect(() => {
        fetchProposals();
        fetchDeals();
        fetchTemplates();
    }, [fetchProposals, fetchTemplates]);

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
        const template = templates.find(t => t._id === templateId);
        if (template) {
            setFormData(prev => ({
                ...prev,
                details: template.content,
                title: template.name // Auto-fill title with template name if needed
            }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTemplateChange = (e) => {
        const { name, value } = e.target;
        setTemplateFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                template: selectedTemplate
            };
            await api.post('/proposals', payload);
            alert('Proposal created successfully!');
            setViewMode('list');
            fetchProposals();
        } catch (error) {
            console.error('Error creating proposal:', error);
            alert('Error creating proposal: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleCreateTemplate = async (e) => {
        e.preventDefault();
        try {
            await api.post('/proposal-templates', templateFormData);
            alert('Template created successfully!');
            setTemplateFormData({ name: '', description: '', content: '', type: 'custom' });
            setViewMode('templates');
            fetchTemplates();
        } catch (error) {
            console.error('Error creating template:', error);
            alert('Error creating template');
        }
    };

    const handleDeleteTemplate = async (id) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            try {
                await api.delete(`/proposal-templates/${id}`);
                fetchTemplates();
            } catch (error) {
                alert('Error deleting template');
            }
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this proposal?')) {
            try {
                await api.delete(`/proposals/${id}`);
                fetchProposals();
            } catch (error) {
                alert('Error deleting proposal');
            }
        }
    };

    const filteredProposals = proposals.filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (viewMode === 'create') {
        return (
            <div className="container proposals-page">
                <div className="proposals-header">
                    <button className="toolbar-btn" onClick={() => setViewMode('list')} style={{ fontSize: '20px' }}>
                        ← Back to List
                    </button>
                    <h1>Create Proposal</h1>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="proposals-content">
                        <div className="card proposal-main-card">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Template Type:</label>
                                    <select
                                        className="form-select"
                                        value={selectedTemplate}
                                        onChange={(e) => handleTemplateSelect(e.target.value)}
                                    >
                                        <option value="">Select a template</option>
                                        {templates.map(t => (
                                            <option key={t._id} value={t._id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Date:</label>
                                    <input
                                        type="date"
                                        name="date"
                                        className="form-input"
                                        value={formData.date}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Valid Till:</label>
                                    <input
                                        type="date"
                                        name="validTill"
                                        className="form-input"
                                        value={formData.validTill}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Client Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="clientName"
                                        className="form-input"
                                        placeholder="Lead/Client Name"
                                        value={formData.clientName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Deal <span className="required">*</span></label>
                                    <select
                                        name="deal"
                                        className="form-select"
                                        value={formData.deal}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Deal</option>
                                        {deals.map(d => (
                                            <option key={d._id} value={d._id}>{d.dealName}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Contact Name</label>
                                    <input
                                        type="text"
                                        name="contactName"
                                        className="form-input"
                                        placeholder="Point of Contact"
                                        value={formData.contactName}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Total Value (₹)</label>
                                    <input
                                        type="number"
                                        name="total"
                                        className="form-input"
                                        placeholder="0.00"
                                        value={formData.total}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Proposal Title <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="form-input"
                                        placeholder="Enter proposal title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <label className="form-group" style={{ marginBottom: '8px', display: 'block' }}>
                                    Proposal Details <span className="required">*</span>
                                </label>
                                <div className="rich-editor-container">
                                    <div className="editor-toolbar">
                                        <button type="button" className="toolbar-btn"><b>B</b></button>
                                        <button type="button" className="toolbar-btn"><i>I</i></button>
                                        <button type="button" className="toolbar-btn"><u>U</u></button>
                                        <button type="button" className="toolbar-btn">≡</button>
                                        <button type="button" className="toolbar-btn">⦙≡</button>
                                        <button type="button" className="toolbar-btn">H▼</button>
                                    </div>
                                    <textarea
                                        name="details"
                                        className="form-textarea"
                                        value={formData.details}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="proposals-footer">
                        <button type="button" className="btn-cancel" onClick={() => setViewMode('list')}>
                            Cancel
                        </button>
                        <button type="button" className="btn-outline" onClick={() => {
                            setTemplateFormData({
                                name: formData.title || 'New Template',
                                description: 'Saved from proposal',
                                content: formData.details,
                                type: 'custom'
                            });
                            setViewMode('create-template');
                        }}>
                            Save as Template
                        </button>
                        <button type="submit" className="btn-submit">
                            Create Proposal
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    if (viewMode === 'templates') {
        return (
            <div className="container proposals-page">
                <div className="proposals-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <button className="toolbar-btn" onClick={() => setViewMode('list')} style={{ fontSize: '20px' }}>
                            ← Back
                        </button>
                        <h1>Proposal Templates</h1>
                    </div>
                    <button className="btn-create" onClick={() => setViewMode('create-template')}>
                        + Create Template
                    </button>
                </div>
                <div className="templates-grid">
                    {templates.length === 0 ? (
                        <div className="card text-center" style={{ gridColumn: '1/-1', padding: '40px' }}>
                            <p>No templates found. Create your first template!</p>
                        </div>
                    ) : (
                        templates.map(template => (
                            <div key={template._id} className="card template-card-large">
                                <div className="template-icon" style={{ fontSize: '48px' }}>📄</div>
                                <h2>{template.name}</h2>
                                <p>{template.description}</p>
                                <div style={{ display: 'flex', gap: '10px', width: '100%', marginTop: 'auto' }}>
                                    <button className="btn-submit" style={{ flex: 1 }} onClick={() => {
                                        handleTemplateSelect(template._id);
                                        setViewMode('create');
                                    }}>
                                        Use
                                    </button>
                                    <button className="btn-icon delete" title="Delete" onClick={() => handleDeleteTemplate(template._id)}>🗑️</button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    }

    if (viewMode === 'create-template') {
        return (
            <div className="container proposals-page">
                <div className="proposals-header">
                    <button className="toolbar-btn" onClick={() => setViewMode('templates')} style={{ fontSize: '20px' }}>
                        ← Back to Templates
                    </button>
                    <h1>Create New Template</h1>
                </div>

                <form onSubmit={handleCreateTemplate}>
                    <div className="card proposal-main-card">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Template Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="name"
                                    className="form-input"
                                    placeholder="e.g., Software Development Proposal"
                                    value={templateFormData.name}
                                    onChange={handleTemplateChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Template Category</label>
                                <select
                                    name="type"
                                    className="form-select"
                                    value={templateFormData.type}
                                    onChange={handleTemplateChange}
                                >
                                    <option value="standard">Standard</option>
                                    <option value="marketing">Marketing</option>
                                    <option value="software">Software</option>
                                    <option value="custom">Custom</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="form-group">
                                <label>Description</label>
                                <input
                                    type="text"
                                    name="description"
                                    className="form-input"
                                    placeholder="Briefly describe what this template is for"
                                    value={templateFormData.description}
                                    onChange={handleTemplateChange}
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <label className="form-group" style={{ marginBottom: '8px', display: 'block' }}>
                                Template Content <span className="required">*</span>
                            </label>
                            <textarea
                                name="content"
                                className="form-textarea"
                                style={{ minHeight: '300px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                value={templateFormData.content}
                                onChange={handleTemplateChange}
                                placeholder="Enter the base content for this template..."
                                required
                            />
                        </div>
                    </div>

                    <div className="proposals-footer">
                        <button type="button" className="btn-cancel" onClick={() => setViewMode('templates')}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-submit">
                            Save Template
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="container proposals-page">
            <div className="info-bar">
                <span>ℹ Proposals are for Leads. If you want to create for existing clients, then create Estimate.</span>
            </div>

            <div className="proposals-actions">
                <div className="left-actions">
                    <button className="btn-create" onClick={() => setViewMode('create')}>
                        <span>+</span> Create Proposal
                    </button>
                    <button className="btn-outline" onClick={() => setViewMode('templates')}>
                        <span style={{ fontSize: '18px' }}>🗂️</span> Proposal Template
                    </button>
                    <button className="btn-outline">
                        📥 Export
                    </button>
                </div>
            </div>

            <div className="card table-card">
                <div className="table-header">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search proposals..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="proposals-table">
                        <thead>
                            <tr>
                                <th>Proposal</th>
                                <th>Deals</th>
                                <th>Contact Name</th>
                                <th>Total</th>
                                <th>Date</th>
                                <th>Valid Till</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                            ) : filteredProposals.length === 0 ? (
                                <tr><td colSpan="8" className="text-center">No data available in table</td></tr>
                            ) : (
                                filteredProposals.map(proposal => (
                                    <tr key={proposal._id}>
                                        <td className="proposal-title-cell">{proposal.title}</td>
                                        <td>{proposal.deal?.dealName || '-'}</td>
                                        <td>{proposal.contactName || '-'}</td>
                                        <td>₹{proposal.total?.toLocaleString() || '0'}</td>
                                        <td>{new Date(proposal.date).toLocaleDateString()}</td>
                                        <td>{proposal.validTill ? new Date(proposal.validTill).toLocaleDateString() : '-'}</td>
                                        <td>
                                            <span className={`status-badge ${proposal.status}`}>
                                                {proposal.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button className="btn-icon" title="View">👁️</button>
                                                <button className="btn-icon delete" title="Delete" onClick={() => handleDelete(proposal._id)}>🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="table-footer">
                    <span>Showing {filteredProposals.length} entries</span>
                </div>
            </div>

            <button className="btn-floating">Hosting Suggestions</button>
        </div>
    );
}

export default Proposals;

